export const M10_UNIT_7 = {
  id: "m10-u7-idioms-storytelling",
  title: "Unit 7: Idioms & Storytelling",
  topic: "Narrative Tenses + 20 Common Idioms",
  iconType: "book-open",
  grammarImage: "/m10-u7-idioms-storytelling.jpg",
  grammarText: "Unit focus: Combining narrative tenses (Past Simple, Past Continuous, Past Perfect) to tell stories — while mastering 20 essential English idioms in real context.",

  sets: [

    // ===================================================================
    // SET 1: SETTING THE SCENE (B1+)
    // Idioms: get cold feet · feel under the weather
    // ===================================================================
    {
      id: "m10-u7-set1",
      title: "Set 1: The Night Before",
      context: `*Paris, Friday evening — Nadia's apartment*

Nadia is an event planner. Tomorrow is the biggest event of her career.
She was sitting at her desk, going through her checklist for the tenth time.
Outside, rain was falling heavily. Inside, her phone kept ringing.
She had been planning this event for six months. And now — she was terrified.

*París, viernes por la tarde — Apartamento de Nadia*

Nadia es organizadora de eventos. Mañana es el evento más grande de su carrera.
Estaba sentada en su escritorio, repasando su lista de verificación por décima vez.
Afuera, llovía con fuerza. Adentro, su teléfono no dejaba de sonar.
Había estado planeando este evento durante seis meses. Y ahora — estaba aterrorizada.`,

      questions: [
        {
          type: "match",
          q: "Match the idioms to their meaning:",
          pairs: [
            { left: "get cold feet", right: "acobardarse / perder el valor" },
            { left: "feel under the weather", right: "sentirse mal" }
          ],
          a: "completed",
          successTip: "'Get cold feet' = acobardarse justo antes de algo importante. 'She got cold feet before the presentation.' You'll use this next! 🥶"
        },

        {
          type: "mc",
          q: "Nadia was sitting at her desk, but she was starting to ___.",
          options: ["get cold feet", "get cold hands", "feel cold inside"],
          a: "get cold feet",
          tip: "Get cold feet = perder el valor antes de algo importante.",
          successTip: "You used it! ✅ 'Get cold feet' = acobardarse. Origin: soldiers literally got cold feet before battle. Now used for weddings, exams, big decisions! 💍"
        },

        {
          type: "mc",
          q: "The scene: 'Rain ___ falling outside, and Nadia ___ through her checklist.'",
          options: ["was / was going", "is / was going", "was / did went"],
          a: "was / was going",
          tip: "Two simultaneous background actions = both Past Continuous."
        },

        {
          type: "mc",
          q: "Her phone ___ ringing all evening — she ___ it three times already.",
          options: ["kept / had answered", "was keeping / answered", "kept / was answering"],
          a: "kept / had answered",
          tip: "Kept + -ing = repeated action. Had answered = earlier completed action.",
          successTip: "'Keep + -ing' = seguir haciendo algo repetidamente. 'She kept calling.' Very natural for repeated annoying actions! 📱"
        },

        {
          type: "mc",
          q: "Nadia's colleague called: 'Are you okay? You sound like you ___  .'",
          options: ["feel under the weather", "feel over the weather", "feel in the weather"],
          a: "feel under the weather",
          tip: "Feel under the weather = sentirse mal / estar pachucho.",
          successTip: "You used it! ✅ 'Under the weather' = bajo el clima. Origin: sailors went below deck when sick during storms. Now just means feeling unwell! ⚓"
        },

        {
          type: "mc",
          q: "While she ___ on the phone, an email ___ in with terrible news.",
          options: ["was talking / came", "talked / was coming", "was talking / was coming"],
          a: "was talking / came",
          tip: "Background (was talking) + sudden event (came in) = Past Continuous + Past Simple."
        },

        {
          type: "mc",
          q: "She ___ the email. Her heart ___. The venue had cancelled.",
          options: ["opened / sank", "was opening / sank", "opened / was sinking"],
          a: "opened / sank",
          tip: "Quick sequence of reactions = Past Simple.",
          successTip: "'Her heart sank' = se le cayó el alma a los pies. One of the most expressive idioms for disappointment! 'When she heard the news, her heart sank.' 💔"
        },

        {
          type: "mc",
          q: "Nadia had ___ working on this event for six months before she received that email.",
          options: ["been", "be", "being"],
          a: "been",
          tip: "Past Perfect Continuous = had been + verb-ing (extended past activity).",
          successTip: "Had BEEN working = Past Perfect Continuous! Shows HOW LONG the activity lasted before the interruption. 'She had been planning for months.' ⏳"
        },

        {
          type: "mc",
          q: "She ___ never felt so panicked in her professional life.",
          options: ["had", "has", "was"],
          a: "had",
          tip: "Had never felt = Past Perfect (up to that past moment)."
        },

        {
          type: "input",
          q: "Nadia tells her friend: 'I was completely fine this morning, but now I ___ ___ ___ ___.'",
          a: "feel under the weather",
          alts: ["Feel under the weather"],
          successTip: "Both idioms from Set 1 now fully used! Cold feet (fear) + under the weather (unwell) = Nadia's complete state right now. Story begins! 🎬"
        }
      ]
    },

    // ===================================================================
    // SET 2: THE STORY BEGINS (B1+)
    // Idioms: head over heels · hit the nail on the head
    // ===================================================================
    {
      id: "m10-u7-set2",
      title: "Set 2: How It All Started",
      context: `*Six months earlier — a cooking class in Lyon*

Before the chaos of the event, there was a beginning.
Nadia had signed up for a cooking class on a rainy Tuesday.
She wasn't expecting anything special. But then Tobias walked in.
He was the chef teaching the class — and she was immediately captivated.

*Seis meses antes — una clase de cocina en Lyon*

Antes del caos del evento, hubo un comienzo.
Nadia se había apuntado a una clase de cocina un martes lluvioso.
No esperaba nada especial. Pero entonces Tobias entró.
Él era el chef que daba la clase — y ella quedó inmediatamente cautivada.`,

      questions: [
        {
          type: "match",
          q: "Match the idioms to their meaning:",
          pairs: [
            { left: "head over heels", right: "perdidamente enamorado/a" },
            { left: "hit the nail on the head", right: "dar en el clavo / acertar exactamente" }
          ],
          a: "completed",
          successTip: "'Head over heels' = completamente enamorado. Origin: falling and tumbling — head where heels should be. Total loss of control! 💘 You'll use this soon!"
        },

        {
          type: "mc",
          q: "When Tobias walked in, Nadia ___ already ___ her apron on and was trying not to stare.",
          options: ["had / put", "has / put", "had / putting"],
          a: "had / put",
          tip: "Earlier completed action = Past Perfect."
        },

        {
          type: "mc",
          q: "While Tobias ___ the recipe, Nadia ___ more attention to him than the food.",
          options: ["was explaining / was paying", "explained / paid", "was explaining / paid"],
          a: "was explaining / was paying",
          tip: "Two simultaneous background actions = both Past Continuous."
        },

        {
          type: "mc",
          q: "Nadia's friend later said: 'You were ___  in love — anyone could see it!'",
          options: ["head over heels", "head over toes", "heels over head"],
          a: "head over heels",
          tip: "Head over heels = perdidamente enamorado/a.",
          successTip: "You used it! ✅ 'Head over heels' is almost always followed by 'in love'. 'She fell head over heels in love.' The most romantic idiom in English! 💕"
        },

        {
          type: "mc",
          q: "Tobias ___ to Nadia after class. He ___ her name wrong on the first try.",
          options: ["came / got", "was coming / got", "came / was getting"],
          a: "came / got",
          tip: "Quick sequence of completed actions = Past Simple."
        },

        {
          type: "match",
          q: "Match the narrative tense to its role in storytelling:",
          pairs: [
            { left: "Past Continuous", right: "Background / ongoing scene" },
            { left: "Past Simple", right: "Events / completed actions" },
            { left: "Past Perfect", right: "Earlier backstory" }
          ],
          a: "completed",
          successTip: "The three-layer cake of storytelling! Continuous = setting, Simple = action, Perfect = backstory. Stack them for rich narratives! 🎂"
        },

        {
          type: "mc",
        q: "Nadia told her friend: 'I think Tobias likes me.' Her friend laughed: 'You ___ — he hasn't stopped looking at you all class.'",
options: ["hit the nail on the head", "hit the point on the head", "hit the nail on the wall"],
a: "hit the nail on the head",

          tip: "Hit the nail on the head = dar en el clavo exactamente.",
          successTip: "You used it! ✅ 'Hit the nail on the head' = acertar perfectamente. Origin: carpentry — hitting a nail exactly right. Used for perfect observations! 🔨"
        },

        {
          type: "mc",
          q: "By the end of the class, Tobias ___ already ___ Nadia his number.",
          options: ["had / given", "has / given", "had / gave"],
          a: "had / given",
          tip: "Give → given. Had given = Past Perfect (by the end)."
        },

        {
          type: "order",
          q: "Traduce: 'Ella estaba lavando los platos cuando él le preguntó si quería cenar.'",
          words: ["She", "was", "washing", "the", "dishes", "when", "he", "asked", "if", "she", "wanted", "to", "have", "dinner."],
          a: "She was washing the dishes when he asked if she wanted to have dinner.",
          extra: ["washed", "was asking", "say"],
          successTip: "Tambien puedes decir: He asked her out to dinner. 'Ask someone out' = invitar a salir. Muy común para citas! 🍽️"
        },

        {
          type: "input",
          q: "Nadia tells Priya: 'Honestly, I ___ ___ ___ ___ from the very first moment I saw him.'",
          a: "was head over heels",
          alts: ["Was head over heels", "fell head over heels"],
          successTip: "Both idioms mastered! ✅ Head over heels (love) + hit the nail on the head (accuracy). Two completely different contexts — that's idiom range! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 3: BACKGROUND & ACTION (B1+)
    // Idioms: lend a hand · be on the ball
    // ===================================================================
    {
      id: "m10-u7-set3",
      title: "Set 3: Chaos in the Kitchen",
      context: `*Berlin, three months later — Tobias's restaurant kitchen*

Tobias was running his restaurant kitchen during a Saturday service.
Twenty tables were waiting. The pasta sauce had burned.
His sous-chef was off sick. Everything was going wrong at once.
Then someone knocked on the kitchen door.

*Berlín, tres meses después — Cocina del restaurante de Tobias*

Tobias dirigía la cocina de su restaurante durante el servicio del sábado.
Veinte mesas esperaban. La salsa de pasta se había quemado.
Su sous-chef estaba enfermo. Todo iba mal a la vez.
Entonces alguien llamó a la puerta de la cocina.`,

      questions: [
        {
          type: "match",
          q: "Match the idioms to their meaning:",
          pairs: [
            { left: "lend a hand", right: "echar una mano / ayudar" },
            { left: "be on the ball", right: "estar alerta / ser eficiente y rápido" }
          ],
          a: "completed",
          successTip: "'Lend a hand' = prestar una mano. Very common offer of help! 'Do you need a hand?' = ¿Necesitas ayuda? Same idea, different wording! 🤝"
        },

        {
          type: "mc",
          q: "Twenty tables ___ for their food, and Tobias ___ the burned sauce.",
          options: ["were waiting / was replacing", "waited / was replacing", "were waiting / replaced"],
          a: "were waiting / was replacing",
          tip: "Simultaneous ongoing actions = both Past Continuous."
        },

        {
          type: "mc",
          q: "The pasta sauce ___ burned because a junior chef ___ the heat too high.",
          options: ["had / have turned", "was / turned", "had / turned"],
          a: "had / turned",
          tip: "The first cause was earlier than the problem = Past Perfect.",
        },

        {
          type: "mc",
          q: "It was Nadia at the door. She had come to ___ .",
          options: ["lend a hand", "give a hand", "offer a hand"],
          a: "lend a hand",
          tip: "Lend a hand = echar una mano / ayudar.",
          successTip: "You used it! ✅ 'Lend a hand' and 'give a hand' mean the same thing in this context. But 'give a hand' ALSO means applause! Context is everything! 👏"
        },

        {
          type: "mc",
          q: "While Nadia ___ the new sauce, Tobias ___ the rest of the kitchen.",
          options: ["was preparing / was managing", "prepared / managing", "prepared / was managing"],
          a: "was preparing / was managing",
          tip: "Two people working simultaneously = two Past Continuous verbs."
        },

        {
          type: "mc",
          q: "Tobias was impressed. Nadia was completely ___  — she never missed a thing.",
          options: ["on the ball", "in the ball", "with the ball"],
          a: "on the ball",
          tip: "On the ball = alerta, eficiente, sin perder detalle.",
          successTip: "You used it! ✅ 'On the ball' = al tanto de todo. Origin: football/soccer — keeping your eye on the ball. 'She's really on the ball today!' ⚽"
        },

        {
          type: "mc",
          q: "By 9pm, all the tables ___ served, and the kitchen ___ finally quiet.",
          options: ["had been / was", "were / had been", "have been / was"],
          a: "had been / was",
          tip: "Completed by that point = Past Perfect. State = Past Simple."
        },

        {
          type: "order",
          q: "Traduce: 'Mientras él preparaba los postres, ella estaba recogiendo la cocina.'",
          words: ["While", "he", "was", "preparing", "the", "desserts,", "she", "was", "cleaning", "up", "the", "kitchen."],
          a: "While he was preparing the desserts, she was cleaning up the kitchen.",
          extra: ["cleaned", "prepared", "cook"]
        },

        {
          type: "mc",
          q: "Tobias said: 'I don't know what I ___ done without you tonight.'",
          options: ["would have", "had", "could"],
          a: "would have",
          tip: "Would have done = past conditional (what might have happened).",
          successTip: "'I don't know what I would have done without you' = No sé qué hubiera hecho sin ti. One of the most natural expressions of gratitude! ❤️"
        },

        {
          type: "input",
          q: "Tobias's review of the evening: 'Nadia was completely ___ ___ ___ — she spotted every problem before I did.'",
          a: "on the ball",
          alts: ["On the ball"],
          successTip: "Lend a hand + on the ball both mastered! ✅ Nadia helped (lend a hand) AND was efficient (on the ball). Two idioms, one character, one scene. Perfect! 🍽️"
        }
      ]
    },

    // ===================================================================
    // SET 4: RACING AGAINST TIME (B2-)
    // Idioms: hear on the grapevine · against the clock
    // ===================================================================
    {
      id: "m10-u7-set4",
      title: "Set 4: The Grapevine Problem",
      context: `*Paris, Friday night — back to the present*

Nadia was still staring at her phone after reading the cancellation email.
She had heard on the grapevine that the new venue was double-booked.
Now she was working against the clock — the event was in 14 hours.
She had to find a solution before dawn.

*París, viernes por la noche — de vuelta al presente*

Nadia seguía mirando su teléfono después de leer el email de cancelación.
Había oído por rumores que la nueva sede estaba doblemente reservada.
Ahora trabajaba contra el reloj — el evento era en 14 horas.
Tenía que encontrar una solución antes del amanecer.`,

      questions: [
        {
          type: "match",
          q: "Match the idioms to their meaning:",
          pairs: [
            { left: "hear on the grapevine", right: "enterarse por rumores" },
            { left: "against the clock", right: "contra el reloj / con tiempo muy limitado" }
          ],
          a: "completed",
          successTip: "'Grapevine' = parra de uva. Rumours travel like whispers along a vine — from person to person. 'I heard it on the grapevine' = me llegó por ahí! 🍇"
        },

        {
          type: "mc",
          q: "Nadia had ___ on the grapevine that the backup venue had problems.",
          options: ["heard", "hear", "hearing"],
          a: "heard",
          tip: "Hear → heard. Had heard = Past Perfect (she knew before the email).",
          successTip: "You used it! ✅ 'Heard on the grapevine' — always HEARD, never 'listened'. Grapevine = informal information network. Very natural in conversation! 🍇"
        },

        {
          type: "mc",
          q: "She ___ working against the clock — 200 guests ___ for a venue that no longer existed.",
          options: ["was / were expecting", "is / expected", "was / expected"],
          a: "was / were expecting",
          tip: "Background actions in progress = Past Continuous."
        },

        {
          type: "mc",
          q: "While she ___ through contacts on her phone, Tobias ___ in.",
          options: ["was scrolling / called", "scrolled / was calling", "was scrolling / was calling"],
          a: "was scrolling / called",
          tip: "Background (scrolling) + sudden action (called) = Continuous + Simple."
        },

        {
          type: "mc",
          q: "Nadia was truly working ___  — every minute counted.",
          options: ["against the clock", "against the time", "against the hours"],
          a: "against the clock",
          tip: "Against the clock = con tiempo muy limitado.",
          successTip: "You used it! ✅ 'Against the clock' = contra reloj. Also: 'race against time' = same meaning. 'It was a race against time to find a new venue!' ⏱️"
        },

        {
          type: "mc",
          q: "Tobias had ___ driving past a beautiful space that very afternoon — an old warehouse.",
          options: ["noticed", "noticing", "notice"],
          a: "noticed",
          tip: "Had noticed = Past Perfect (earlier that day, before the call)."
        },

        {
          type: "order",
          q: "Traduce: 'Ella había oído por rumores que el lugar estaba disponible.'",
          words: ["She", "had", "heard", "on", "the", "grapevine", "that", "the", "venue", "was", "available."],
          a: "She had heard on the grapevine that the venue was available.",
          extra: ["saw", "listened", "knew"],
          successTip: "Use had heard, not had listened, because hear means receiving information passively, while listen implies deliberate attention and usually takes to"
        },

        {
          type: "mc",
          q: "Tobias said: 'I ___ the owner this morning — he ___ me it might be available.'",
          options: ["met / told", "met / had told", "was meeting / told"],
          a: "met / told",
          tip: "If it was the same day, we can use Past Simple for both",
          successTip: "Using the Past Perfect for an event described as “this morning” on the same day is unusual."
        },

        {
          type: "mc",
          q: "It was 2am. They ___ been working non-stop for three hours when they finally ___ a solution.",
          options: ["had / found", "have / found", "had / were finding"],
          a: "had / found",
          tip: "Had been working = duration. Found = the sudden result = Past Simple."
        },

        {
          type: "input",
          q: "Nadia's diary: 'I was ___ ___ ___ ___ all night — but we made it work.'",
          a: "working against the clock",
          alts: ["working against the clock", "worked against the clock"],
          successTip: "Grapevine (information by rumour) + against the clock (time pressure). Nadia's night was intense! ⏰🍇"
        }
      ]
    },

    // ===================================================================
    // SET 5: PEOPLE & PERSONALITIES (B2-)
    // Idioms: pain in the neck · keep an eye on
    // ===================================================================
    {
      id: "m10-u7-set5",
      title: "Set 5: The VIP Guest",
      context: `*Saturday morning — the new venue*

The event was a high-profile product launch.
Priya had been sent to cover it as a journalist.
But there was one problem: the client, Monsieur Beaumont, was notoriously difficult.
"Keep an eye on him," Nadia had warned Tobias earlier.

*Sábado por la mañana — el nuevo lugar*

El evento era un lanzamiento de producto de alto perfil.
Priya había sido enviada a cubrirlo como periodista.
Pero había un problema: el cliente, Monsieur Beaumont, era notoriamente difícil.
"Vigílalo," Nadia le había advertido a Tobias antes.`,

      questions: [
        {
          type: "match",
          q: "Match the idioms to their meaning:",
          pairs: [
            { left: "a pain in the neck", right: "una molestia / un pesado" },
            { left: "keep an eye on", right: "vigilar / estar pendiente de" }
          ],
          a: "completed",
          successTip: "'A pain in the neck' = muy molesto/pesado. Also: 'a pain in the backside' = same, slightly stronger! 'He's such a pain in the neck at meetings!' 😤"
        },

        {
          type: "mc",
          q: "Priya had ___ covering events like this for five years — she knew how they worked.",
          options: ["been", "be", "being"],
          a: "been",
          tip: "Had been covering = Past Perfect Continuous (extended experience)."
        },

        {
          type: "mc",
          q: "Nadia had warned Tobias to ___  Monsieur Beaumont at all times.",
          options: ["keep an eye on", "keep a look on", "watch an eye on"],
          a: "keep an eye on",
          tip: "Keep an eye on = vigilar / estar pendiente de.",
          successTip: "You used it! ✅ 'Keep an eye on' = vigilar con atención. 'Can you keep an eye on my bag?' = ¿Puedes vigilar mi bolsa? Super useful in daily life! 👁️"
        },

        {
          type: "mc",
          q: "While Tobias ___ the canapés, Monsieur Beaumont ___ over and started complaining.",
          options: ["was arranging / came", "arranged / was coming", "was arranging / was coming"],
          a: "was arranging / came",
          tip: "Background (arranging) + sudden arrival (came) = Continuous + Simple."
        },

        {
          type: "mc",
          q: "Priya wrote in her notes: 'The client was being a real ___ — nothing was good enough.'",
          options: ["pain in the neck", "pain in the back", "pain in the head"],
          a: "pain in the neck",
          tip: "Pain in the neck = una molestia / un pesado.",
          successTip: "You used it! ✅ 'Being a pain in the neck' = portándose como un pesado. 'Being' is key here — it describes behaviour in the moment! 😤"
        },

        {
          type: "mc",
          q: "Monsieur Beaumont ___ already complained four times before lunch had even started.",
          options: ["had", "has", "was"],
          a: "had",
          tip: "Before lunch = even earlier = Past Perfect."
        },

        {
          type: "mc",
          q: "Tobias ___ an eye on him all morning, just as Nadia had told him.",
          options: ["kept", "had kept", "was keeping"],
          a: "kept",
          tip: "Completed action in the narrative = Past Simple.",
          successTip: "'Kept an eye on him' = estuvo pendiente de él. Notice: keep → kept (irregular). 'I kept an eye on things while she was away.' Very natural! 👁️"
        },

        {
          type: "order",
          q: "Traduce: 'Nadia le había pedido a Tobias que vigilara al cliente durante el evento.'",
          words: ["Nadia", "had", "asked", "Tobias", "to", "keep", "an", "eye", "on", "the", "client", "during", "the", "event."],
          a: "Nadia had asked Tobias to keep an eye on the client during the event.",
          extra: ["watch", "keeping", "had"]
        },

        {
          type: "mc",
          q: "Priya caught Nadia's eye across the room and mouthed: '___ he always like this?'",
          options: ["Is", "Was", "Has"],
          a: "Is",
          tip: "General present character question = Present Simple.",
          successTip: "'Caught her eye' = llamó su atención / cruzaron miradas. Also: 'catch someone's eye' = intentar atraer su atención! 👀"
        },

        {
          type: "input",
          q: "Nadia whispered back: 'He's a complete ___ ___ ___ ___ — but he's also our biggest client.'",
          a: "pain in the neck",
          alts: ["Pain in the neck","Pain in the back","Pain in the head","pain in the ass"],
          successTip: "Pain in the neck (difficult person) + keep an eye on (watch carefully). Monsieur Beaumont needed BOTH responses! 😄"
        }
      ]
    },

    // ===================================================================
    // SET 6: DIFFICULT DECISIONS (B2-)
    // Idioms: heart of gold · sit on the fence
    // ===================================================================
    {
      id: "m10-u7-set6",
      title: "Set 6: Tobias's Big Decision",
      context: `*Saturday afternoon — a quiet corner of the venue*

Between the starter and the main course, Tobias had a problem.
Two restaurant groups had offered him head chef positions that very week.
One was in Tokyo. One was in London — where Nadia was based.
He had been sitting on the fence for days. But today, he had to decide.

*Sábado por la tarde — un rincón tranquilo del lugar*

Entre el entrante y el plato principal, Tobias tenía un problema.
Dos grupos de restaurantes le habían ofrecido puestos de chef ese mismo semana.
Uno era en Tokio. Uno era en Londres — donde Nadia estaba basada.
Llevaba días sin decidirse. Pero hoy tenía que decidir.`,

      questions: [
        {
          type: "match",
          q: "Match the idioms to their meaning:",
          pairs: [
            { left: "heart of gold", right: "un corazón de oro / muy buena persona" },
            { left: "sit on the fence", right: "no decidirse / mantenerse neutral" }
          ],
          a: "completed",
          successTip: "'Sit on the fence' = quedarse en el medio sin decidir. 'Stop sitting on the fence and make a choice!' Very common in arguments and decisions! 🤔"
        },

        {
          type: "mc",
          q: "Tobias had been ___  for days — Tokyo or London?",
          options: ["sitting on the fence", "standing on the fence", "sitting at the fence"],
          a: "sitting on the fence",
          tip: "Sit on the fence = no decidirse.",
          successTip: "You used it! ✅ 'Sitting on the fence' = sin posición clara. Also: 'fence-sitter' = persona que nunca se decide. 'He's such a fence-sitter!' 🚧"
        },

        {
          type: "mc",
          q: "While Tobias ___ his options, Priya ___ him on the terrace.",
          options: ["was considering / found", "considered / was finding", "was considering / was finding"],
          a: "was considering / found",
          tip: "Background thinking (was considering) + sudden discovery (found) = Continuous + Simple."
        },

        {
          type: "mc",
          q: "Priya had known Tobias for years. She always said he had a real ___ .",
          options: ["heart of gold", "soul of gold", "mind of gold"],
          a: "heart of gold",
          tip: "Heart of gold = corazón de oro (very kind person).",
          successTip: "You used it! ✅ 'Heart of gold' = ser muy buena persona por dentro. 'She seems tough, but she has a heart of gold.' Great for describing complex characters! 💛"
        },

        {
          type: "mc",
          q: "Priya said: 'You ___ sitting on the fence long enough — which city feels like home?'",
          options: ["have been", "had been", "were"],
          a: "have been",
          tip: "Up to the present moment = Present Perfect Continuous.",
          successTip: "'You have been sitting on the fence' = llevas tiempo sin decidirte. Present Perfect Continuous = duration from past to NOW. Not the same as 'had been'! ⏰"
        },

        {
          type: "mc",
          q: "Before that conversation, Tobias ___ never said his feelings out loud.",
          options: ["had", "has", "was"],
          a: "had",
          tip: "Never said before that moment = Past Perfect.",
          successTip: "Say something out loud = decir algo en voz alta. 'I never said it out loud, but I was really nervous.' Very natural expression! 🗣️"
        },

        {
          type: "mc",
          q: "Tobias looked across the room at Nadia, who ___ still managing a crisis with a smile.",
          options: ["was", "had been", "is"],
          a: "was",
          tip: "Ongoing action Tobias could see = Past Continuous."
        },

        {
          type: "order",
          q: "Traduce: 'Él había estado indeciso durante días, pero finalmente tomó una decisión.'",
          words: ["He", "had", "been", "sitting", "on", "the", "fence", "for", "days,", "but", "he", "finally", "made", "a", "decision."],
          a: "He had been sitting on the fence for days, but he finally made a decision.",
          extra: ["decided", "sitting", "fence"]
        },

        {
          type: "mc",
          q: "Priya smiled: 'She has a ___  — and so do you. London. That's my advice.'",
          options: ["heart of gold", "face of gold", "will of gold"],
          a: "heart of gold",
          tip: "Heart of gold = very kind and genuine person.",
          successTip: "Use 'so do you' for the same statement; use 'you too' for short replies like greetings or wishes."
        },

        {
          type: "input",
          q: "Tobias made his decision. He texted London: 'I'm in.' He had finally stopped ___ ___ ___ ___.",
          a: "sitting on the fence",
          alts: ["Sitting on the fence"],
          successTip: "Both Set 6 idioms closed! Heart of gold (Priya's insight into Tobias) + sitting on the fence (his indecision). Priya hit the nail on the head — again! 🎯💛"
        }
      ]
    },

    // ===================================================================
    // SET 7: SCHOOL DAYS FLASHBACK (B2)
    // Idioms: teacher's pet · cut corners
    // ===================================================================
    {
      id: "m10-u7-set7",
      title: "Set 7: Amara's Flashback",
      context: `*Saturday afternoon — Amara arrives at the event*

Amara had been invited as a guest — and to give advice on the new venue's design.
Waiting in the lobby, she let her mind wander back to architecture school.
She remembered a classmate who had always been the teacher's pet.
And how he had cut corners on every project — and eventually faced the consequences.

*Sábado por la tarde — Amara llega al evento*

Amara había sido invitada como invitada — y para dar consejos sobre el diseño del nuevo lugar.
Esperando en el vestíbulo, dejó que su mente vagara hasta la escuela de arquitectura.
Recordó a un compañero que siempre había sido el favorito del profesor.
Y cómo siempre había hecho las cosas a medias en cada proyecto — y al final afrontó las consecuencias.`,

      questions: [
        {
          type: "match",
          q: "Match the idioms to their meaning:",
          pairs: [
            { left: "teacher's pet", right: "el favorito/consentido del profesor" },
            { left: "cut corners", right: "hacer las cosas a medias / ahorrar esfuerzo" }
          ],
          a: "completed",
          successTip: "'Cut corners' = hacer algo de manera rápida y descuidada, saltándose pasos. 'They cut corners on the construction — now it's falling apart.' Very common warning! ⚠️"
        },

        {
          type: "mc",
          q: "While Amara ___ in the lobby, she ___ thinking about her old classmate, Rémi.",
          options: ["was waiting / was", "waited / was", "was waiting / had been"],
          a: "was waiting / was",
          tip: "Two simultaneous actions = both Past Continuous."
        },

        {
          type: "mc",
          q: "Rémi had always been the ___ in architecture school — the professor loved him.",
          options: ["teacher's pet", "teacher's favourite", "teacher's child"],
          a: "teacher's pet",
          tip: "Teacher's pet = el favorito/consentido del profesor.",
          successTip: "You used it! ✅ 'Teacher's pet' = consentido del profe. Always slightly negative — implies unfair favouritism. 'Don't be such a teacher's pet!' 🐾"
        },

        {
          type: "mc",
          q: "Despite his status, Rémi had been ___ on every single project.",
          options: ["cutting corners", "cutting edges", "cutting paths"],
          a: "cutting corners",
          tip: "Cut corners = hacer las cosas a medias, saltándose pasos.",
          successTip: "You used it! ✅ 'Cutting corners' = taking shortcuts at the expense of quality. Origin: literally cutting the corner of a route to save distance! 📐"
        },

        {
          type: "mc",
          q: "While other students ___ long hours in the studio, Rémi ___ always early.",
          options: ["were working / left", "worked / was leaving", "were working / was leaving"],
          a: "were working / left",
          tip: "Others working (background) + Rémi's repeated action = Continuous + Simple."
        },

        {
          type: "mc",
          q: "By the time their final project ___ submitted, Rémi's structural calculations ___ several errors.",
          options: ["was / had contained", "had been / contained", "was / contained"],
          a: "was / had contained",
          tip: "Submission = Past Simple. Errors = discovered before/during = Past Perfect.",
          successTip: "A structural calculation error in architecture is no joke! In real life, cutting corners in construction can be genuinely dangerous. 🏗️"
        },

        {
          type: "mc",
          q: "The professor ___ for the whole class when he found the errors in Rémi's work.",
          options: ["called", "had called", "was calling"],
          a: "called",
          tip: "Single past action in the story = Past Simple.",
          successTip: "Here, 'call for' means to summon or ask someone to come."
        },

        {
          type: "order",
          q: "Traduce: 'Aunque era el favorito del profesor, había estado haciendo las cosas a medias durante todo el año.'",
          words: ["Although", "he", "was", "the", "teacher's", "pet,", "he", "had", "been", "cutting", "corners", "all", "year."],
          a: "Although he was the teacher's pet, he had been cutting corners all year.",
        },

        {
          type: "mc",
          q: "Amara remembered thinking: 'Being the ___  won't save you if your work isn't honest.'",
          options: ["teacher's pet", "teacher's choice", "teacher's star"],
          a: "teacher's pet",
          tip: "Teacher's pet = consentido del profesor."
        },

        {
          type: "input",
          q: "Amara smiled to herself. She had never ___ ___ — and she was proud of that.",
          a: "cut corners",
          alts: ["Cut corners"],
          successTip: "Both Set 7 idioms mastered in a complete flashback narrative! Teacher's pet + cut corners — two school idioms, one cautionary tale. Real storytelling! 📚"
        }
      ]
    },

    // ===================================================================
    // SET 8: FACING CONSEQUENCES (B2)
    // Idioms: over the top · face the music
    // ===================================================================
    {
      id: "m10-u7-set8",
      title: "Set 8: When Things Explode",
      context: `*Saturday evening — the event, mid-dinner*

Everything had been going smoothly — until Monsieur Beaumont stood up.
He was giving a speech. And it was going very, very wrong.
"This is completely over the top," Priya whispered to Amara.
Someone was going to have to face the music.

*Sábado por la noche — el evento, a mitad de la cena*

Todo había ido bien — hasta que Monsieur Beaumont se puso de pie.
Estaba dando un discurso. Y estaba saliendo muy, muy mal.
"Esto es completamente exagerado," susurró Priya a Amara.
Alguien iba a tener que afrontar las consecuencias.`,

      questions: [
        {
          type: "match",
          q: "Match the idioms to their meaning:",
          pairs: [
            { left: "over the top", right: "exagerado / demasiado extremo" },
            { left: "face the music", right: "afrontar las consecuencias / asumir la responsabilidad" }
          ],
          a: "completed",
          successTip: "'Face the music' = afrontar las consecuencias. Origin: a military tradition where soldiers faced a drumbeat as they were dismissed. Very dramatic! 🥁"
        },

        {
          type: "mc",
          q: "Monsieur Beaumont had been speaking for twenty minutes when he ___ crying on stage.",
          options: ["started", "had started", "was starting"],
          a: "started",
          tip: "Sudden new event after background = Past Simple."
        },

        {
          type: "mc",
          q: "Priya whispered: 'This is completely ___  — even for him.'",
          options: ["over the top", "over the edge", "over the line"],
          a: "over the top",
          tip: "Over the top = exagerado / demasiado extremo.",
          successTip: "You used it! ✅ 'Over the top' = OTT in British slang! 'That reaction was a bit OTT.' Very commonly abbreviated. Means excessive, dramatic, too much! 🎭"
        },

        {
          type: "mc",
          q: "While Beaumont ___ , Nadia ___ desperately to signal to Tobias from across the room.",
          options: ["was speaking / was trying", "spoke / was trying", "was speaking / tried"],
          a: "was speaking / was trying",
          tip: "Two simultaneous ongoing actions = both Past Continuous.",
          successTip: "She was desperately trying can also be correct, but 'was desperately trying' is more natural and 'was trying desperately' adds extra emphasis."
        },

        {
          type: "mc",
          q: "Tobias ___ the situation immediately — he had been watching the whole time.",
          options: ["read", "was reading", "had read"],
          a: "read",
          tip: "Single decisive action = Past Simple.",
          successTip: "'Read the situation' = leer la situación / entender lo que pasa. Very natural idiom. 'She reads the room perfectly.' = entiende el ambiente de inmediato! 📖"
        },

        {
          type: "mc",
          q: "Tobias walked calmly to the stage. He knew it was time to ___  .",
          options: ["face the music", "hear the music", "play the music"],
          a: "face the music",
          tip: "Face the music = afrontar las consecuencias.",
          successTip: "You used it! ✅ 'Face the music' = enfrentarse a lo que viene, aunque sea difícil. 'Eventually, you'll have to face the music.' Very commonly used! 🎵"
        },

        {
          type: "mc",
          q: "Tobias ___ the microphone gently from Beaumont and guided him off stage.",
          options: ["took", "was taking", "had taken"],
          a: "took",
          tip: "Decisive completed action in the story = Past Simple."
        },

        {
          type: "order",
          q: "Traduce: 'Después de que él se había ido, la sala quedó en silencio durante un momento.'",
          words: ["After", "he", "had", "gone,", "the", "room", "fell", "silent", "for", "a", "moment."],
          a: "After he had gone, the room fell silent for a moment.",
        },

        {
          type: "mc",
          q: "Amara said quietly: 'That speech was ___  . Even Beaumont must know it.'",
          options: ["over the top", "over the edge", "above the top"],
          a: "over the top",
          tip: "Over the top = exagerado / demasiado extremo."
        },

        {
          type: "input",
          q: "Nadia exhaled slowly. Tobias had stepped in so she didn't have to ___ ___ ___ alone.",
          a: "face the music",
          alts: ["Face the music"],
          successTip: "Both Set 8 idioms mastered through drama! Over the top (Beaumont's speech) + face the music (Tobias's courage). One scene, two idioms, real tension! 🎬"
        }
      ]
    },

    // ===================================================================
    // SET 9: THE MORNING AFTER (B2+)
    // Idioms: with a pinch of salt · let your hair down
    // ===================================================================
    {
      id: "m10-u7-set9",
      title: "Set 9: The Morning After",
      context: `*Sunday morning — a café near the venue*

The event was over. It had been chaotic, dramatic, and somehow — a success.
All four sat together over coffee, talking about the night before.
"Someone told me Beaumont actually loved the whole thing," said Soren.
Priya raised an eyebrow. "I'd take that with a pinch of salt."

*Domingo por la mañana — una cafetería cerca del lugar*

El evento había terminado. Había sido caótico, dramático y de alguna manera — un éxito.
Los cuatro se sentaron juntos con un café, hablando de la noche anterior.
"Alguien me dijo que a Beaumont en realidad le encantó todo," dijo Soren.
Priya levantó una ceja. "Yo me lo tomaría con reservas."`,

      questions: [
        {
          type: "match",
          q: "Match the idioms to their meaning:",
          pairs: [
            { left: "with a pinch of salt", right: "con reservas / sin creerlo del todo" },
            { left: "let your hair down", right: "relajarse / soltarse / divertirse sin inhibiciones" }
          ],
          a: "completed",
          successTip: "'With a pinch of salt' = con reservas. Don't believe it completely. 'Take his opinions with a pinch of salt.' Very intellectual! 🧂"
        },

        {
          type: "mc",
          q: "Priya said: 'I'd take any story about Beaumont being happy ___  .'",
          options: ["with a pinch of salt", "with a grain of rice", "with a bit of salt"],
          a: "with a pinch of salt",
          tip: "With a pinch of salt = con reservas / sin creerlo todo.",
          successTip: "You used it! ✅ Note: 'take it with a grain of salt' is also correct — American English prefers 'grain'. British prefers 'pinch'. Both are standard! 🧂"
        },

        {
          type: "mc",
          q: "Amara laughed: 'Last night was insane. But after everything — didn't we all just  ___ a little?'",
          options: ["let our hair down", "put our hair down", "let our hair out"],
          a: "let our hair down",
          tip: "Let your hair down = relajarse y divertirse sin inhibiciones.",
          successTip: "You used it! ✅ 'Let your hair down' = soltarse el pelo / relajarse. Origin: formal occasions required pinned-up hair — letting it down = informality! 💇"
        },

        {
          type: "mc",
          q: "While they ___ their coffees, Nadia ___ telling the story of the venue crisis.",
          options: ["were drinking / was", "drank / was", "were drinking / had been"],
          a: "were drinking / was",
          tip: "Simultaneous background actions = both Past Continuous."
        },

        {
          type: "mc",
          q: "Soren: 'I ___ never have imagined the evening going that way — but it ___ out perfectly.'",
          options: ["would / worked", "had / worked", "could / was working"],
          a: "would / worked",
          tip: "Would never have imagined = past conditional. Worked out = Past Simple.",
          successTip: "'Work out' = resultar bien. 'It worked out in the end!' = ¡Al final salió bien! One of the most reassuring phrases in English! 🌟"
        },

        {
          type: "mc",
          q: "Priya: 'When Tobias ___ the stage, I was ready to ___ for the exit.'",
          options: ["took / head", "was taking / head", "took / go"],
          a: "took / head",
          tip: "Past Simple + head for = dirigirse a.",
          successTip: "'Head for the exit' = dirigirse a la salida. Also idiom: 'heading for disaster' = rumbo al desastre. 'Head for' is a very flexible phrasal verb! 🚪"
        },

        {
          type: "order",
          q: "Traduce: 'Lo habían pasado genial, aunque todo había salido de manera inesperada.'",
          words: ["They", "had", "had", "a", "great", "time,", "even", "though", "everything", "had", "gone", "unexpectedly."],
          a: "They had had a great time, even though everything had gone unexpectedly.",
        },

        {
          type: "mc",
          q: "Tobias smiled: 'I heard on the grapevine that Beaumont is recommending us to his entire network.' Priya: '___  on that one.'",
          options: ["I'd take that with a pinch of salt", "I'd believe that completely", "I'd trust that for sure"],
          a: "I'd take that with a pinch of salt",
          tip: "With a pinch of salt = con reservas / sin creerlo del todo.",
          successTip: "Notice: grapevine AND pinch of salt in the same sentence! 🔄"
        },

        {
          type: "mc",
          q: "After weeks of stress, Nadia ___ finally let her hair down — she had been smiling all morning.",
          options: ["had", "has", "was"],
          a: "had",
          tip: "Completed relaxation by that point = Past Perfect."
        },

        {
          type: "input",
          q: "Soren said: 'I'd always heard Parisian event planners were uptight. I'll take that stereotype ___ ___ ___ ___ ___ now.'",
          a: "with a pinch of salt",
          alts: ["With a pinch of salt"],
          successTip: "Both Set 9 idioms mastered! Pinch of salt (healthy scepticism) + let your hair down (well-earned relaxation). Perfect Sunday morning! ☀️🧂"
        }
      ]
    },

    // ===================================================================
    // SET 10: THE FULL STORY (B2+)
    // Idioms: pulling someone's leg · the ball is in your court
    // ===================================================================
    {
      id: "m10-u7-set10",
      title: "Set 10: The Complete Picture",
      context: `*Sunday afternoon — the venue terrace*

Tobias had something to tell Nadia.
He had been rehearsing it all morning.
He had accepted the London job. He was staying.
But first — he needed to know if she was serious about them.

*Domingo por la tarde — la terraza del lugar*

Tobias tenía algo que decirle a Nadia.
Lo había estado ensayando toda la mañana.
Había aceptado el trabajo en Londres. Se quedaba.
Pero primero, necesitaba saber si ella hablaba en serio sobre ellos.`,

      questions: [
        {
          type: "match",
          q: "Match the final idioms to their meaning:",
          pairs: [
            { left: "pull someone's leg", right: "tomar el pelo a alguien / bromear" },
            { left: "the ball is in your court", right: "la decisión es tuya / te toca a ti" },
             { left: "sitting on the fence", right: "estar indeciso" }
          ],
          a: "completed",
          successTip: "'The ball is in your court' = la pelota está en tu tejado. Origin: tennis — when the ball crosses the net, the OTHER player must act. Powerful! 🎾"
        },

        {
          type: "mc",
          q: "Tobias told Nadia he had taken the London job. She stared at him: '___ you ___ ?'",
          options: ["Are / pulling my leg", "Are / pulling my hair", "Are / joking my leg"],
          a: "Are / pulling my leg",
          tip: "Pulling someone's leg = tomar el pelo / bromear.",
          successTip: "You used it! ✅ 'Are you pulling my leg?' = ¿Me estás tomando el pelo? Used when something seems too good (or too strange) to be true! 🦵😄"
        },

        {
          type: "mc",
          q: "Tobias: 'I'm not ___ . I've already signed the contract.'",
          options: ["pulling your leg", "pulling your hair", "pulling your arm"],
          a: "pulling your leg",
          tip: "Pulling your leg = tomando el pelo.",
          successTip: "Negative form: 'I'm not pulling your leg!' = ¡No te estoy tomando el pelo! This negative version is actually MORE common than the positive! 🦵"
        },

        {
          type: "mc",
          q: "While Tobias ___ , Nadia ___ very still, processing everything.",
          options: ["was talking / sat", "talked / was sitting", "was talking / was sitting"],
          a: "was talking / sat",
          tip: "Background (talking) + deliberate settled action (sat) = Continuous + Simple."
        },

        {
          type: "mc",
          q: "Tobias said: 'I've made my decision. Now ___ .'",
          options: ["the ball is in your court", "the ball is your problem", "the choice is in your court"],
          a: "the ball is in your court",
          tip: "The ball is in your court = la decisión es tuya ahora.",
          successTip: "You used it! ✅ 'The ball is in your court' = te toca a ti decidir. Perfect for endings — negotiations, relationships, job offers. Powerful handover phrase! 🎾"
        },

        {
          type: "mc",
          q: "Nadia had been waiting for this moment longer than she ___ admit.",
          options: ["would", "had", "could"],
          a: "would",
          tip: "Longer than she would admit = más de lo que admitiría."
        },

        {
          type: "order",
          q: "Traduce: 'Él había estado ensayando las palabras toda la mañana, pero cuando llegó el momento, las olvidó todas.'",
          words: ["He", "had", "been", "rehearsing", "the", "words", "all", "morning,", "but", "when", "the", "moment", "came,", "he", "forgot", "them", "all."],
          a: "He had been rehearsing the words all morning, but when the moment came, he forgot them all.",
          tip: "Rehearsing = ensayando las palabras. Moment = momento." 
        },

        {
          type: "mc",
          q: "From across the terrace, Priya ___ watching the whole thing. She ___ already ___ what Nadia's answer would be.",
          options: ["was / had / known", "had / was / knowing", "was / has / known"],
          a: "was / had / known",
          tip: "Watching = Past Continuous. Had known = Past Perfect (she knew before)."
        },

        {
          type: "mc",
          q: "Nadia finally smiled. 'I'm not ___ when I say yes — I mean it.'",
          options: ["pulling your leg", "telling a lie", "making it up"],
          a: "pulling your leg",
          tip: "Pulling your leg = tomar el pelo.",
          successTip: "'I'm not pulling your leg' used for sincerity! When something sounds unbelievable but is true — Nadia uses it to confirm she's genuine! 🦵💕"
        },

        {
          type: "input",
          q: "Priya wrote in her notebook: 'Six months of chaos. One event. Two people who had been ___ ___ ___ ___ the whole time — and finally stopped.'",
          a: "sitting on the fence",
          alts: ["Sitting on the fence"],
          successTip: "🏆 UNIT 7 COMPLETE! All 20 idioms mastered ✅ All three narrative tenses combined ✅ Characters, storylines, drama ✅ You tell stories like a native speaker! 🎬🌍"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (B2)
  // ===================================================================
  readingSet: {
    id: "m10-u7-reading",
    title: "Reading: One Night, Four Stories",
    category: "reading",

    questions: [

      // --- PASSAGE 1 ---
      {
        type: "passage",
        passage: `**Nadia — The Longest Night**

Nadia had been working against the clock since 2am. By the time the guests arrived, she had already solved three major problems. While she was greeting people at the door, she still felt under the weather — but nobody could tell.`
      },
      {
        type: "mc",
        q: "When had Nadia started working against the clock?",
        options: ["Since 2am", "Since midnight", "Since the morning"],
        a: "Since 2am",
        tip: "'...working against the clock since 2am.'"
      },
      {
        type: "mc",
        q: "How did Nadia feel while greeting guests?",
        options: ["Under the weather", "Head over heels", "On the ball"],
        a: "Under the weather",
        tip: "'...she still felt under the weather.'"
      },

      // --- PASSAGE 2 ---
      {
        type: "passage",
        passage: `**Tobias — The Chef Who Stayed Calm**

While Monsieur Beaumont was making his speech, Tobias had been keeping an eye on the room. He had seen this kind of situation before. When the moment came, he didn't hesitate. He walked to the stage and faced the music.`
      },
      {
        type: "mc",
        q: "What had Tobias been doing during Beaumont's speech?",
        options: ["Keeping an eye on the room", "Preparing desserts", "Talking to Priya"],
        a: "Keeping an eye on the room",
        tip: "'...had been keeping an eye on the room.'"
      },
      {
        type: "mc",
        q: "What did Tobias do when the moment came?",
        options: ["Faced the music", "Got cold feet", "Sat on the fence"],
        a: "Faced the music",
        tip: "'He walked to the stage and faced the music.'"
      },

      // --- PASSAGE 3 ---
      {
        type: "passage",
        passage: `**Priya — The Observer**

Priya had been taking notes all evening. She had heard on the grapevine that the venue had nearly been cancelled the night before. She took that story with a pinch of salt — until Nadia confirmed it herself over breakfast.`
      },
      {
        type: "mc",
        q: "How had Priya heard about the near-cancellation?",
        options: ["On the grapevine", "From Tobias", "From Beaumont"],
        a: "On the grapevine",
        tip: "'She had heard on the grapevine...'"
      },
      {
        type: "mc",
        q: "How did Priya first treat the story?",
        options: ["With a pinch of salt", "Head over heels", "Against the clock"],
        a: "With a pinch of salt",
        tip: "'She took that story with a pinch of salt.'"
      },

      // --- PASSAGE 4 ---
      {
        type: "passage",
        passage: `**Amara — The Architect's Eye**

While everyone else had been watching the drama, Amara had been studying the building. The warehouse had a heart of gold beneath its rough exterior. She had already sketched three redesign ideas on her napkin before the main course arrived.`
      },
      {
        type: "mc",
        q: "What had Amara been doing while others watched the drama?",
        options: ["Studying the building", "Talking to Priya", "Helping Tobias"],
        a: "Studying the building",
        tip: "'...Amara had been studying the building.'"
      },
      {
        type: "mc",
        q: "How is the warehouse described?",
        options: ["A heart of gold beneath its rough exterior", "Derelict and beyond saving", "Over the top in its design"],
        a: "A heart of gold beneath its rough exterior",
        tip: "'The warehouse had a heart of gold beneath its rough exterior.'"
      },

      // --- PASSAGE 5 ---
      {
        type: "passage",
        passage: `**The Morning After — Four Friends**

Over coffee, they all finally let their hair down. Soren said Beaumont had loved the event. Priya raised an eyebrow. Tobias was pulling no one's leg when he said he'd taken the London job. And Nadia? She had stopped sitting on the fence at last.`
      },
      {
        type: "mc",
        q: "What did Tobias confirm at breakfast?",
        options: ["He had taken the London job", "He was moving to Tokyo", "He had quit catering"],
        a: "He had taken the London job",
        tip: "'Tobias was pulling no one's leg when he said he'd taken the London job.'"
      },
      {
        type: "mc",
        q: "What had Nadia finally stopped doing?",
        options: ["Sitting on the fence", "Cutting corners", "Getting cold feet"],
        a: "Sitting on the fence",
        tip: "'She had stopped sitting on the fence at last.'",
        successTip: "All 20 idioms woven through a single story! That's Unit 7 complete — idioms learned in context, used in context, remembered through story! 🎬🌟"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u7-review",
    title: "Unit 7 Review: Idioms & Storytelling",
    category: "review",

    questions: [
      {
        type: "mc",
        q: "Review: She ___ cold feet just before the big presentation.",
        options: ["got", "had", "felt"],
        a: "got",
        tip: "Get cold feet = acobardarse (always with GET)."
      },
      {
        type: "mc",
        q: "Review: He was completely ___ ___ ___ in love — he couldn't think straight.",
        options: ["head over heels", "heels over head", "head over toes"],
        a: "head over heels",
        tip: "Head over heels = perdidamente enamorado."
      },
      {
        type: "mc",
        q: "Review: While the guests ___ arriving, Nadia ___ already solved three problems.",
        options: ["were / had", "was / had", "were / have"],
        a: "were / had",
        tip: "Background Continuous + earlier Past Perfect."
      },
      {
        type: "mc",
        q: "Review: I heard ___ ___ ___ that the restaurant is closing.",
        options: ["on the grapevine", "in the grapevine", "from the grapevine"],
        a: "on the grapevine",
        tip: "Hear ON the grapevine = enterarse por rumores."
      },
      {
        type: "mc",
        q: "Review: He's been ___ ___ ___ for weeks — Tokyo or London?",
        options: ["sitting on the fence", "standing on the fence", "sitting at the fence"],
        a: "sitting on the fence",
        tip: "Sitting on the fence = sin decidirse."
      },
      {
        type: "mc",
        q: "Review: Take anything he says ___ ___ ___ ___ ___ .",
        options: ["with a pinch of salt", "with a grain of pepper", "with a touch of salt"],
        a: "with a pinch of salt",
        tip: "With a pinch of salt = con reservas."
      },
      {
        type: "mc",
        q: "Review: They had been ___ corners all year — and now the project had failed.",
        options: ["cutting", "slicing", "trimming"],
        a: "cutting",
        tip: "Cut corners = hacer las cosas a medias."
      },
      {
        type: "mc",
        q: "Review: You've heard my decision. Now ___ ___ ___ ___ ___ ___ .",
        options: ["the ball is in your court", "the choice is in your hands", "the move is yours now"],
        a: "the ball is in your court",
        tip: "The ball is in your court = la decisión es tuya."
      },
      {
        type: "mc",
        q: "Review: I'm not ___ ___ ___ — this is the most beautiful place I've ever been.",
        options: ["pulling your leg", "pulling your arm", "pulling your hair"],
        a: "pulling your leg",
        tip: "Pulling your leg = tomando el pelo."
      },
      {
        type: "mc",
        q: "Review: After months of stress, she finally ___ ___ ___ ___ at the party.",
        options: ["let her hair down", "put her hair down", "took her hair down"],
        a: "let her hair down",
        tip: "Let your hair down = relajarse y soltarse.",
        successTip: "🎉 UNIT 7 MASTERED! All 20 idioms ✅ Narrative tenses ✅ Storytelling ✅ You now think, speak and write in idioms! Ready for Unit 8! 🚀🌍"
      }
    ]
  }
};
